import { demoFacilities } from '@/lib/demoData';

import type { DirectoryUserWithRoles } from '@/models';

const USER_SEEDS = [
  { name: 'Avery Stone', email: 'avery.stone', roles: ['Org Admin', 'Compliance Lead'] },
  { name: 'Jordan Reed', email: 'jordan.reed', roles: ['Regional Ops'] },
  { name: 'Morgan Lee', email: 'morgan.lee', roles: ['Director of Rehab'] },
  { name: 'Taylor Brooks', email: 'taylor.brooks', roles: ['Billing Admin'] },
  { name: 'Casey Nguyen', email: 'casey.nguyen', roles: ['Clinical Manager'] },
  { name: 'Riley Patel', email: 'riley.patel', roles: ['Facility Admin'] },
  { name: 'Parker James', email: 'parker.james', roles: ['Therapy Lead'] },
  { name: 'Quinn Foster', email: 'quinn.foster', roles: ['MDS Coordinator'] },
  { name: 'Skyler Diaz', email: 'skyler.diaz', roles: ['Scheduler'] },
  { name: 'Cameron Blake', email: 'cameron.blake', roles: ['Read Only'] },
] as const;

function hash(input: string): number {
  let value = 0;
  for (let index = 0; index < input.length; index += 1) {
    value = Math.imul(value, 31) + input.charCodeAt(index);
  }
  return Math.abs(value);
}

// Mirrors EHR's UserAccountStatus, which has exactly these two members.
function pickStatus(seed: number): string {
  return seed % 7 === 0 ? 'unclaimed' : 'active';
}

export function demoOrgUsers(orgId: string): DirectoryUserWithRoles[] {
  const orgFacilities = demoFacilities()
    .filter((facility) => facility.organizationId === orgId)
    .slice(0, 6);
  const count = 6 + (hash(orgId) % 3);
  const offset = hash(`${orgId}:org-users`) % USER_SEEDS.length;

  return Array.from({ length: count }, (_, index) => {
    const seed = USER_SEEDS[(offset + index) % USER_SEEDS.length];
    const isOrgAdmin = index % 3 === 0;
    const assignedFacilities = isOrgAdmin
      ? orgFacilities.slice(0, Math.min(orgFacilities.length, 4)).map((facility) => facility.displayName)
      : orgFacilities
          .filter((_, facilityIndex) => facilityIndex % 2 === index % 2)
          .slice(0, 3)
          .map((facility) => facility.displayName);

    return {
      userId: `${orgId}-user-${String(index + 1).padStart(2, '0')}`,
      displayName: seed.name,
      emailAddress: `${seed.email}@demo.caredocs.ai`,
      status: pickStatus(hash(`${orgId}:${seed.email}`)),
      roles: isOrgAdmin ? [...seed.roles, 'Org Reporting'] : [...seed.roles],
      assignedFacilities,
    } satisfies DirectoryUserWithRoles;
  });
}

export function demoFacilityUsers(facilityId: string): DirectoryUserWithRoles[] {
  const facility = demoFacilities().find((row) => row.facilityId === facilityId);
  const count = 4 + (hash(facilityId) % 3);
  const offset = hash(`${facilityId}:facility-users`) % USER_SEEDS.length;

  return Array.from({ length: count }, (_, index) => {
    const seed = USER_SEEDS[(offset + index) % USER_SEEDS.length];
    const roles = index === 0 ? [...seed.roles, 'Facility Admin'] : [...seed.roles];

    return {
      userId: `${facilityId}-user-${String(index + 1).padStart(2, '0')}`,
      displayName: seed.name,
      emailAddress: `${seed.email}@demo.caredocs.ai`,
      status: pickStatus(hash(`${facilityId}:${seed.email}`)),
      roles,
      assignedFacilities: facility ? [facility.displayName] : [],
    } satisfies DirectoryUserWithRoles;
  });
}
